(() => {
  if (customElements.get('eaura-size-guide')) return;
  const sizeTable = table => /\b(size|sizes|bust|chest|waist|hips?|inseam)\b/i.test(table.textContent);
  const chartNote = node => /^(?:(?:(?:tops?|pants?|shorts?|trousers?|bottoms?|skirts?|dress|dresses|jackets?|coats?)\s+)?size (?:guide|chart)|(?:product|garment) measurements|\*?\s*(?:all )?measurements|\*?\s*(?:high|medium|low|no|non[- ]?)\s*stretch|UK size guide:|UK sizing guidance:|Size label:|How to measure:|all measurements are|we recommend sizing based)/i.test(node.textContent.trim());
  function chartNodes(root) {
    const tables = Array.from(root.querySelectorAll('table')).filter(sizeTable);
    const result = new Set(tables);
    for (const table of tables) {
      // Only adjoining headings/measurement notes travel with the table.
      for (const direction of ['previousElementSibling', 'nextElementSibling']) {
        let node = table[direction]; let count = 0;
        while (node && count++ < 8 && !node.querySelector('table')) {
          if (!node.textContent.trim() || chartNote(node)) {result.add(node);node = node[direction];}
          else break;
        }
      }
    }
    return Array.from(result).sort((a,b) => a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);
  }
  function safeClone(nodes) {
    const host = document.createElement('div');
    for (const node of nodes) host.append(node.cloneNode(true));
    host.querySelectorAll('script,style,iframe,object,embed,form,input,button,svg,math,link,meta').forEach(el=>el.remove());
    const allowed = new Set(['TABLE','THEAD','TBODY','TFOOT','TR','TH','TD','CAPTION','P','BR','STRONG','B','EM','I','H2','H3','H4','UL','OL','LI','DIV','SPAN']);
    for(const el of Array.from(host.querySelectorAll('*'))) {
      if(!allowed.has(el.tagName)){el.replaceWith(...el.childNodes);continue;}
      for(const attr of Array.from(el.attributes))if(!['colspan','rowspan'].includes(attr.name)||!/^\d{1,2}$/.test(attr.value))el.removeAttribute(attr.name);
    }
    return host;
  }
  // Present source tables uniformly; never infer or change a size/measurement value.
  function normaliseCharts(host) {
    const text = cell => cell.textContent.replace(/\s+/g,' ').trim();
    for (const table of host.querySelectorAll('table')) {
      const rows=Array.from(table.rows);
      if(!rows.length)continue;
      const first=Array.from(rows[0].cells);
      const headerDepth=Math.max(1,...first.map(c=>c.rowSpan));
      if(headerDepth>3 || rows.length<=headerDepth)continue;
      const grid=[];
      let valid=true;
      rows.forEach((row,r)=>{
        grid[r] ||= [];
        let col=0;
        for(const cell of row.cells){
          while(grid[r][col])col++;
          for(let y=r;y<r+cell.rowSpan;y++){
            grid[y] ||= [];
            for(let x=col;x<col+cell.colSpan;x++){
              if(grid[y][x])valid=false;
              grid[y][x]=cell;
            }
          }
          col+=cell.colSpan;
        }
      });
      const width=grid[0].length;
      if(!valid || grid.some(row=>row.length!==width || Array.from({length:width},(_,i)=>row[i]).some(c=>!c)))continue;
      const headings=Array.from({length:width},(_,i)=>{
        const cells=[...new Set(grid.slice(0,headerDepth).map(row=>row[i]))];
        return cells.map(text).filter(Boolean).join(' · ').replace(/_/g,' ');
      });
      const size=headings.findIndex(h=>/^sizes?$/i.test(h));
      const uk=headings.findIndex(h=>/\bUK\b/i.test(h));
      const order=Array.from({length:width},(_,i)=>i);
      if(size>=0 && uk>=0 && size!==uk){order.splice(order.indexOf(size),1);order.splice(order.indexOf(uk),1);order.unshift(size,uk);}
      const replacement=document.createElement('table');
      if(table.caption)replacement.append(table.caption.cloneNode(true));
      const head=replacement.createTHead().insertRow();
      for(const i of order){
        const cell=document.createElement('th');cell.scope='col';
        cell.textContent=i===uk?'UK sizes':i===size?'Size':headings[i];head.append(cell);
      }
      const body=replacement.createTBody();
      for(let r=headerDepth;r<rows.length;r++){
        const row=body.insertRow();
        // Summary rows such as Elasticity retain their original spans and order.
        if(Array.from(rows[r].cells).some(c=>c.colSpan>1 || c.rowSpan>1) || new Set(grid[r]).size!==width){
          for(const cell of rows[r].cells)row.append(cell.cloneNode(true));
        }else for(const i of order){
          const cell=row.insertCell();let value=text(grid[r][i]);
          if(i===size && uk>=0 && uk!==size){
            const combined=value.match(/^UK\s*(\d+(?:\s*[-–—/]\s*\d+)?)\s*\(([^)]+)\)$/i);
            const normalise=v=>v.replace(/^UK\s*/i,'').replace(/\s*\((?:estimate|approx\.)\)/gi,'').replace(/[–—/]/g,'-').replace(/\s/g,'');
            if(combined && normalise(combined[1])===normalise(text(grid[r][uk])))value=combined[2];
          }
          cell.textContent=value;
        }
      }
      table.replaceWith(replacement);
    }
    // Remove the spacer paragraphs supplied by older charts, including summary rows.
    host.querySelectorAll('td p,th p').forEach(p=>{if(!p.textContent.trim())p.remove();});
  }
  class EauraSizeGuide extends HTMLElement {
    connectedCallback() {
      if(this.ready)return;
      const init=()=>requestAnimationFrame(()=>this.initialise());
      if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',init,{once:true});else init();
    }
    initialise() {
      if(this.ready||!this.isConnected)return;
      const dialog=this.querySelector('dialog'),trigger=this.querySelector('.eaura-size-guide__trigger');
      if(!dialog||typeof dialog.showModal!=='function')return;
      const saved=this.querySelector('[data-eaura-chart]').content;
      const fallback=this.querySelector('[data-eaura-description-source]').content;
      // Existing products work without migration; new app drafts also store a chart metafield.
      const source=Array.from(fallback.querySelectorAll('table')).some(sizeTable)?fallback:saved;
      const nodes=chartNodes(source);
      if(!nodes.some(n=>n.tagName==='TABLE'))return;
      const cleaned=safeClone(nodes);
      normaliseCharts(cleaned);
      this.querySelector('.eaura-size-guide__body').replaceChildren(...cleaned.childNodes);
      this.ready=true;trigger.hidden=false;
      trigger.addEventListener('click',()=>{this.previousOverflow=document.documentElement.style.overflow;dialog.showModal();document.documentElement.style.overflow='hidden';});
      this.querySelector('.eaura-size-guide__close').addEventListener('click',()=>dialog.close());
      dialog.addEventListener('click',event=>{const r=dialog.getBoundingClientRect();if(event.target===dialog&&(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom))dialog.close();});
      dialog.addEventListener('close',()=>{document.documentElement.style.overflow=this.previousOverflow||'';if(trigger.isConnected)trigger.focus();});
      const product=this.closest('product-info');
      if(!product)return;
      for(const description of product.querySelectorAll('[data-eaura-product-description]')) {
        chartNodes(description).forEach(node=>node.remove());
        description.querySelectorAll('p').forEach(p=>{if(!p.textContent.trim()&&!p.querySelector('img,video,iframe'))p.remove();});
        const textNodes=document.createTreeWalker(description,NodeFilter.SHOW_TEXT);
        let text;
        while((text=textNodes.nextNode())) text.textContent=text.textContent.replace(/size (?:guide|chart) above/gi,'size chart beside the size options');
        const heading=description.closest('collapsible-tab')?.querySelector('.m-collapsible--button > span');
        if(heading&&/^Description\s*&\s*Size Chart$/i.test(heading.textContent.trim()))heading.textContent='Description';
      }
      // Hide the theme's alternate FoxKit control only after our own chart is available.
      this.parentElement.querySelectorAll('[data-open-sizeguide]').forEach(button=>{button.hidden=true;button.style.display='none';});
    }
    disconnectedCallback(){if(this.querySelector('dialog')?.open)document.documentElement.style.overflow=this.previousOverflow||'';}
  }
  customElements.define('eaura-size-guide',EauraSizeGuide);
})();
